import React from "react";
import AppContext from "../../contexts/AppContext";
import i18n from "../../i18n";
import Loader from "../components/Loader";
import routes from "../../routes";
import ErrorView from "../components/ErrorView";
import {logEvent, userEvents} from "../../utils/log";
import * as Sentry from "@sentry/browser";
// eslint-disable-next-line no-unused-vars
import adapter from "webrtc-adapter";

const ERROR_TYPE_SUPPORT = "support";
const ERROR_TYPE_PERMISSION = "permission";
const ERROR_TYPE_NODEVICE = "nodevice";
const ERROR_TYPE_UNKNOWN = "unknown";

const INITIAL_CONSTRAINTS_SIZES = [720, 640, 480];

const STOP_CAMERA_INTERVAL = 5000;

export class PhotoCaptureError extends Error {
  constructor() {
    super();
    this.name = "PhotoCaptureError";
    this.code = -1;
    this.message = i18n.t("error_modal__camera_message");
  }
}

export default class PhotoCapturePage extends React.Component {
  state = {
    isShowPopup: false,
    isLoading: true,
    isErrored: false,
    errorType: null,
    image: null
  };

  constructor(props) {
    super(props);

    this.captureDevice = null;

    this.videoRef = React.createRef();
    this.videoStreamURL = null;

    this.shadowCanvas = document.createElement("canvas");
    this.shadowCanvasContext = this.shadowCanvas.getContext("2d");

    this.constraints = INITIAL_CONSTRAINTS_SIZES;
  }

  componentDidMount() {
    logEvent(userEvents.PAGE_PHOTOCAPTURE);
    this.startCamera();
  }

  componentWillUnmount() {
    this.stopCamera();
  }

  startCamera = () => {
    clearTimeout(this.stopCameraTimer);

    this.setState({
      isLoading: true
    }, () => {
      if (typeof window.navigator.mediaDevices === "undefined"
          || !window.navigator.mediaDevices.getUserMedia) {
        const err = new Error("WEBRTC not supported");
        err.name = "NotSupported";
        this.handleError(err, "camera");
        return;
      }

      const requestSize = this.constraints.shift();

      let videoConstraints = window.isWebviewIOS
        ? {width: requestSize, height: requestSize}
        : {width: {min: requestSize}, height: {min: requestSize}};

      this.stopCameraTimer = setTimeout(() => {
        const err = new Error("Timeout was reached");
        err.name = "NotSupported";

        this.handleError(err, "timeout");
      }, STOP_CAMERA_INTERVAL);

      // try to catch 'then of undefined'
      try {
        window.navigator.mediaDevices.getUserMedia({video: videoConstraints})
          .then(this.handleCameraSuccess)
          .catch((err) => {
            if ((err.name === "OverconstrainedError" || err.name === "NotReadableError") && this.constraints.length > 0) {
              this.startCamera();
            } else {
              window.navigator.mediaDevices.getUserMedia({video: true})
                .then(this.handleCameraSuccess)
                .catch((err) => {
                  this.handleError(err, "camera");
                });
            }
          });
      } catch(e) {
        const err = new Error("StartCamera error");
        err.name = "NotSupported";
        this.handleError(err, "camera");
      }
    });
  };

  stopCamera = () => {
    if (this.captureDevice) {
      this.captureDevice.stop();
      this.captureDevice = null;
    }

    if (this.videoStreamURL) {
      URL.revokeObjectURL(this.videoStreamURL);
    }
  };

  handleError = (err, source) => {
    clearTimeout(this.stopCameraTimer);

    //console.log(err, source);

    let errorType;
    if (err.name === "NotSupported") {
      errorType = ERROR_TYPE_SUPPORT;
    } else if (err.name === "NotAllowedError") {
      errorType = ERROR_TYPE_PERMISSION;
    } else if (err.name === "NotFoundError") {
      errorType = ERROR_TYPE_NODEVICE;
    } else {
      errorType = ERROR_TYPE_UNKNOWN;
    }

    logEvent(userEvents.PHOTO_CAPTURE__ERROR, errorType, source);

    if (errorType === ERROR_TYPE_UNKNOWN) {
      Sentry.captureException(err);
    }

    this.setState({
      isLoading: false,
      isErrored: true,
      errorType: errorType,
    });

    this.stopCamera();
  };

  handleCameraSuccess = (stream) => {
    clearTimeout(this.stopCameraTimer);

    this.setState({
      isLoading: false,
      isErrored: false
    });

    this.captureDevice = stream.getVideoTracks()[0];

    if (HTMLMediaElement) { // ios 11 fix
      this.videoRef.current.srcObject = stream;
    } else {
      this.videoStreamURL = URL.createObjectURL(this.stream);
      this.videoRef.current.src = this.videoStreamURL;
    }

    this.constraints = INITIAL_CONSTRAINTS_SIZES;
  };

  handleCaptureButtonClick = () => {
    logEvent(userEvents.PHOTO_CAPTURE__CAPTURE_CLICK);

    this.setState({
      isLoading: true
    }, () => {
      try {
        console.log(this.videoRef);

        this.shadowCanvas.width = this.videoRef.current.videoWidth;
        this.shadowCanvas.height = this.videoRef.current.videoHeight;
        this.shadowCanvasContext.drawImage(this.videoRef.current, 0, 0);

        this.setState({
          isLoading: false,
          image: this.shadowCanvas.toDataURL("image/jpeg")
        });
      } catch (e) {
        this.handleError(e, "capture");
      }
    });
  };

  handleResetButtonClick = () => {
    logEvent(userEvents.PHOTO_CAPTURE__RESET_CLICK);

    this.setState({image: null});
  };

  handleApplyButtonClick = () => {
    this.stopCamera();

    logEvent(userEvents.PHOTO_CAPTURE__APPLY_CLICK);

    this.setState({
      isLoading: true,
    }, () => {
      this.props.history.replace({
        pathname: routes.PROCESSING,
        search: window.location.search,
        state: {file: this.state.image}
      });
    });
  };

  handleFileSelected = (file) => {
    logEvent(userEvents.PHOTO_SELECT, {
      page: "photocapture",
    });

    this.setState({
      isLoading: true,
    }, () => {
      this.props.history.replace({
        pathname: routes.PROCESSING,
        search: window.location.search,
        state: {file: file}
      });
    });
  };

  handleBackButtonClick = () => {
    this.props.history.replace({
      pathname: routes.INDEX,
      search: window.location.search,
    });
  };

  handleTryAgainButtonClick = () => {
    window.location.reload();
  };

  render() {
    const cropSize = window.innerWidth > window.innerHeight - 240
      ? window.innerHeight - 240
      : window.innerWidth;

    const containerClassNames = "capture-container " + (this.state.image && " captured");

    return <React.Fragment>
      {this.state.isLoading && <Loader message={i18n.t("loader_processing")}/>}
      {this.state.isErrored && <ErrorView error={(new PhotoCaptureError())} onFileSelected={this.handleFileSelected} />}
      <main hidden={this.state.isLoading || this.state.isErrored}>
        <div className="crop-foto cropper">
          <div className={containerClassNames} style={{height: cropSize}}>
            <button className="back-button" onClick={this.handleBackButtonClick}>
              <SvgArrowBackIcon />
            </button>
            {!this.state.isErrored &&
              <React.Fragment>
                <video className="capture-holder" playsInline autoPlay muted controls={false} ref={this.videoRef} />
                {this.state.image && <img
                  className="capture-image"
                  src={this.state.image}
                  alt="preview"
                  onLoad={() => URL.revokeObjectURL(this.state.image)} />}
              </React.Fragment>
            }
          </div>

          <div className="notify-container">
            {this.state.image
              ?
              <button
                hidden={!this.state.image}
                className="next-button"
                children={i18n.t('photo_capture_page__button_retake')}
                onClick={this.handleResetButtonClick} />
              :
              <div>
                <h3 children={i18n.t('photo_capture_page__notify')} />
                <ul>
                  <li className="danger-item">
                    <SvgGlassesIcon className="icon-glasses" />
                    <p dangerouslySetInnerHTML={{__html: i18n.t('no_glasses_on')}}/>
                  </li>
                  <li className="danger-item">
                    <SvgFaceIcon className="icon-face" />
                    <p dangerouslySetInnerHTML={{__html: i18n.t('face_is_not_covered')}}/>
                  </li>
                  <li className="success-item">
                    <SvgLightningIcon className="icon-lightning" />
                    <p dangerouslySetInnerHTML={{__html: i18n.t('good_lignting')}}/>
                  </li>
                </ul>
              </div>
            }
          </div>

          <div className="w-button">
            <button
              hidden={this.state.image}
              className={"foto-button " + (this.state.isErrored ? "-disabled" : "")}
              onClick={this.handleCaptureButtonClick} />
            <button
              hidden={!this.state.image}
              className="next-button"
              onClick={this.handleApplyButtonClick}>
              <SvgDoneIcon className="icon-done" />
              {i18n.t('photo_capture_page__button_apply')}
            </button>
          </div>
        </div>
      </main>
    </React.Fragment>;
  }
}

PhotoCapturePage.contextType = AppContext;

function SvgArrowBackIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 20 20">
    <path fill="#222" d="M7.854 15.387c.407.407.407 1.047 0 1.454-.407.393-1.047.393-1.454 0L.305 10.747C.116 10.56 0 10.297 0 10.006c0-.29.116-.553.305-.742L6.4 3.17c.407-.407 1.047-.407 1.454 0 .407.407.407 1.047 0 1.454L3.49 8.974h15.492c.567-.002 1.018.464 1.018 1.031s-.45 1.018-1.018 1.018H3.492l4.362 4.364z"/>
  </svg>;
}

function SvgGlassesIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 48 48">
     <g clip-path="url(#prefix__clip0)">
        <path fill="#222" d="M23.989 42c-6.398 0-11.603-5.205-11.603-11.602v-.169h-.253c-1.973 0-3.578-1.605-3.578-3.578v-1.638c0-1.973 1.605-3.579 3.578-3.579h.253v-5.273c0-2.021.936-3.943 2.517-5.196-.057-.273-.087-.556-.087-.842 0-.737.393-1.494 1.05-2.024l.02-.016.398-.284c.014-.01.029-.02.044-.028.062-.033.132-.076.212-.124.375-.225.942-.565 1.88-.85 1.292-.425 2.656-.673 4.164-.758 5.777-.323 10.922 1.377 12.512 4.137.204.328.342.582.477.871 1.3 2.795.242 5.73.018 6.287v4.1h.277c1.973 0 3.578 1.606 3.578 3.579v1.638c0 1.223-.617 2.305-1.556 2.95l.323.874c.082.221-.031.467-.252.548-.22.082-.466-.03-.548-.252l-.285-.77c-.392.147-.817.228-1.26.228h-.277v.169C35.59 36.794 30.386 42 23.989 42zm-10.75-12.197v.595c0 5.927 4.822 10.75 10.75 10.75 5.927 0 10.75-4.823 10.75-10.75v-3.615h-.756c-.114 0-.223-.045-.303-.126-.08-.081-.125-.19-.123-.305 0-.05.051-4.96-.329-6.48-.343-1.374-1.324-1.922-1.366-1.945-.101-.056-.177-.15-.208-.262-.03-.112-.015-.232.044-.331.005-.01.585-1.003.61-1.954.004-.15-.628-.522-1.457-.752-.903-.187-1.592-.175-1.61-.175H20.361c-1.282 0-2.39-.22-3.293-.655-.151-.073-.297-.155-.436-.246-.398.46-.965 1.246-.963 1.838.002.942.6 1.928.607 1.938.061.1.079.222.048.335-.031.114-.108.21-.212.265-.039.021-1.02.57-1.363 1.944-.38 1.52-.329 6.43-.328 6.48.001.114-.043.224-.124.305-.08.08-.189.126-.303.126h-.755v3.02zm22.375-.427h.254c.34 0 .664-.062.964-.176l-.267-.722c-.082-.221.03-.466.252-.548.22-.082.466.031.547.252l.218.587c.617-.5 1.011-1.264 1.011-2.118v-1.638c0-1.503-1.222-2.725-2.725-2.725h-.254v7.088zm-23.481-7.089c-1.503 0-2.726 1.223-2.726 2.726v1.638c0 1.503 1.223 2.725 2.726 2.725h.253v-7.089h-.253zm22.279 3.644h.326v-8.573c-.371-.81-.945-1.395-1.584-1.816-.05.746-.34 1.46-.536 1.871.43.336 1.136 1.047 1.438 2.252.34 1.364.36 5.004.356 6.266zm-21.173 0h.326c-.003-1.262.016-4.902.357-6.266.3-1.201 1.002-1.91 1.433-2.249-.217-.44-.537-1.223-.539-2.024-.002-.905.72-1.877 1.156-2.381-.328-.335-.597-.73-.792-1.165-1.224 1.09-1.94 2.665-1.94 4.316v9.769zm16.068-12.345c.578 0 4.018.109 5.755 2.611.06-.23.12-.497.17-.792-.5-.488-2.307-2.083-4.996-2.594-1.88-.357-4.1-.22-6.248-.085-1.597.1-3.248.203-4.71.091-1.418-.107-2.4-.477-3.073-.891.308.47.737.855 1.252 1.1.783.372 1.76.56 2.903.56h8.947zM27.38 11.73c1.044 0 2.062.062 3.014.243 2.363.45 4.073 1.638 4.949 2.38.015-.376.004-.773-.046-1.18-1.189-1.19-3.297-2.02-6.11-2.403-1.49-.203-3.189.028-4.987.272-1.813.247-3.686.502-5.373.28-1.87-.246-2.727-.92-3.117-1.523-.027.108-.042.217-.042.323l.003.12c.138.269.926 1.516 3.67 1.724 1.404.107 2.953.01 4.593-.092 1.145-.07 2.31-.144 3.446-.144zm.573-1.89c.463 0 .914.026 1.348.085 2.384.324 4.31.968 5.665 1.883-.05-.134-.105-.268-.167-.401-.12-.26-.246-.488-.433-.79l-.01-.017c-1.413-2.45-6.341-4.012-11.726-3.71-1.432.081-2.725.316-3.953.719l-.016.005c-.838.257-1.346.562-1.683.765-.082.049-.155.092-.22.128l-.367.262c-.05.041-.098.084-.143.128.053.336.409 1.28 2.69 1.58 1.573.207 3.39-.04 5.146-.28 1.295-.176 2.623-.356 3.87-.356zm-5.06 24.127c-.225 0-.414-.178-.425-.405-.011-.236.17-.436.405-.447.03-.002 3.006-.16 4.957-1.287.204-.117.465-.048.583.156.118.204.048.465-.156.583-2.133 1.231-5.212 1.393-5.342 1.4h-.021zm7.73-5.523h-1.897c-2.186 0-3.965-1.78-3.965-3.966v-.823h-1.419v.823c0 2.187-1.779 3.965-3.965 3.965H17.48c-1.187 0-2.153-.966-2.153-2.153v-1.974c0-.835.679-1.514 1.513-1.514h3.01l-2.898-1.396c-.212-.103-.302-.358-.2-.57.103-.212.358-.301.57-.199l4.491 2.165h4.33l4.491-2.165c.213-.102.467-.013.57.2.102.212.013.466-.2.569l-2.897 1.396h3.156c.834 0 1.513.68 1.513 1.514v1.974c0 1.188-.966 2.154-2.153 2.154zm-5.01-4.789v.823c0 1.716 1.397 3.113 3.113 3.113h1.897c.717 0 1.3-.584 1.3-1.3v-1.975c0-.364-.296-.66-.66-.66H25.614zm-8.773 0c-.364 0-.66.297-.66.661v1.974c0 .718.583 1.3 1.3 1.3h1.897c1.716 0 3.112-1.395 3.112-3.112v-.823h-5.649zm12.003 3.575c-.103 0-.206-.037-.288-.112-.174-.16-.185-.429-.026-.602l1.977-2.152c.159-.174.429-.185.602-.026.174.16.185.43.026.603l-1.977 2.152c-.084.091-.199.137-.314.137zm-9.921 0c-.103 0-.207-.037-.288-.112-.174-.16-.185-.429-.026-.602l1.977-2.152c.159-.174.429-.185.602-.026.174.16.185.43.025.603l-1.976 2.152c-.084.091-.199.137-.314.137zm8.421-.838c-.103 0-.206-.037-.288-.112-.174-.16-.185-.43-.026-.602l1.426-1.553c.16-.174.43-.185.603-.026.173.16.185.43.025.603l-1.426 1.552c-.084.092-.199.138-.314.138zm-9.921 0c-.103 0-.207-.037-.289-.112-.173-.16-.184-.43-.025-.603l1.426-1.552c.16-.174.43-.185.603-.026.173.16.184.43.025.603l-1.426 1.552c-.084.092-.199.138-.314.138z"/>
    </g>
    <path fill="#E7504E" d="M24 0C10.766 0 0 10.765 0 24c0 13.234 10.766 24 24 24s24-10.766 24-24C48 10.765 37.234 0 24 0zM3 24c0-5.25 1.95-10.045 5.148-13.731L37.73 39.852C34.045 43.05 29.251 45 24 45 12.421 45 3 35.579 3 24zm36.852 13.731L10.27 8.148C13.955 4.95 18.75 3 24 3c11.58 0 21 9.421 21 21 0 5.25-1.95 10.045-5.148 13.731z"/>
    <defs>
        <clipPath id="prefix__clip0">
            <path fill="#fff" d="M6 6H42V42H6z"/>
        </clipPath>
    </defs>
  </svg>;
}

function SvgFaceIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 48 48">
    <g clip-path="url(#prefix__clip0)">
      <path fill="#222" d="M23.989 42c-6.398 0-11.603-5.205-11.603-11.602v-.169h-.253c-1.973 0-3.578-1.605-3.578-3.578v-1.638c0-1.973 1.605-3.578 3.578-3.578h.254v-5.273c0-2.022.935-3.944 2.516-5.196-.057-.274-.087-.557-.087-.843 0-.737.393-1.494 1.05-2.024l.02-.016.398-.284c.014-.01.029-.02.044-.027.062-.034.132-.076.212-.125.375-.225.942-.565 1.88-.85 1.292-.425 2.656-.673 4.164-.758 5.777-.324 10.926 1.378 12.514 4.14l.002.003c.205.332.34.581.473.865 1.3 2.796.242 5.73.018 6.288v4.1h.277c1.973 0 3.578 1.605 3.578 3.578v1.638c0 1.973-1.605 3.578-3.578 3.578h-.277v.169C35.591 36.794 30.386 42 23.99 42zm-10.75-12.197v.595c0 5.927 4.822 10.749 10.75 10.749 5.927 0 10.75-4.822 10.75-10.75v-3.613h-.756c-.114 0-.223-.046-.303-.127-.08-.081-.125-.19-.123-.305 0-.05.051-4.96-.329-6.48-.343-1.374-1.324-1.922-1.365-1.945-.102-.056-.178-.15-.209-.262-.03-.112-.015-.232.044-.331.005-.01.585-1.003.61-1.954.005-.15-.629-.522-1.458-.752-.903-.187-1.591-.175-1.609-.175H20.361c-1.282 0-2.39-.22-3.293-.655-.151-.073-.297-.155-.436-.245-.398.46-.965 1.245-.963 1.837.002.949.6 1.927.607 1.937.062.1.08.222.048.336-.03.114-.107.21-.211.265-.04.022-1.02.57-1.364 1.944-.38 1.521-.329 6.431-.328 6.48.001.114-.043.224-.123.305-.08.081-.19.127-.303.127h-.756v3.019zm22.375-.427h.254c1.503 0 2.725-1.222 2.725-2.725v-1.638c0-1.503-1.222-2.726-2.725-2.726h-.254v7.09zm-23.481-7.088c-1.503 0-2.726 1.222-2.726 2.725v1.638c0 1.503 1.223 2.725 2.726 2.725h.254v-7.088h-.254zm22.279 3.643h.326v-8.573c-.371-.81-.945-1.395-1.584-1.816-.05.746-.339 1.46-.536 1.871.43.337 1.136 1.047 1.438 2.252.34 1.364.36 5.004.356 6.266zm-21.173 0h.327c-.004-1.262.015-4.902.356-6.266.3-1.201 1.002-1.91 1.433-2.249-.216-.44-.537-1.223-.539-2.024-.002-.905.72-1.877 1.156-2.382-.328-.335-.597-.728-.792-1.164-1.224 1.09-1.94 2.665-1.94 4.316v9.769zm16.068-12.345c.578 0 4.019.109 5.755 2.611.06-.23.12-.497.17-.792-.5-.488-2.307-2.083-4.996-2.594-1.88-.358-4.1-.22-6.248-.085-1.596.1-3.248.202-4.71.091-1.418-.108-2.401-.478-3.073-.892.308.471.737.856 1.252 1.101.783.372 1.76.56 2.903.56h8.947zM27.38 11.73c1.044 0 2.062.061 3.014.242 2.363.45 4.073 1.638 4.949 2.38.015-.376.004-.773-.046-1.18-1.19-1.19-3.297-2.02-6.11-2.403-1.49-.203-3.189.028-4.987.273-1.812.246-3.686.501-5.373.28-1.87-.247-2.727-.92-3.117-1.524-.027.109-.042.217-.042.324l.003.12c.138.268.926 1.515 3.67 1.724 1.404.106 2.954.01 4.593-.093 1.145-.07 2.311-.143 3.446-.143zm.573-1.89c.463 0 .914.025 1.349.084 2.383.324 4.31.968 5.664 1.883-.05-.134-.105-.268-.167-.401-.12-.259-.246-.487-.433-.79l-.01-.016c-1.414-2.452-6.343-4.011-11.726-3.71-1.432.08-2.725.315-3.952.717l-.017.006c-.838.257-1.346.562-1.683.764-.082.05-.155.093-.22.13l-.367.261c-.05.04-.097.084-.143.128.054.336.409 1.28 2.69 1.58 1.573.207 3.39-.04 5.146-.28 1.296-.175 2.623-.356 3.87-.356zM23.99 38.286c-1.968 0-3.845-.901-5.278-2.332-.053-.035-.096-.08-.129-.132-1.489-1.548-2.47-3.678-2.53-5.95-.004-.167.09-.322.241-.394.15-.073.33-.051.46.056 1.644 1.376 4.078 2.074 7.236 2.074 3.157 0 5.592-.698 7.237-2.074.128-.107.308-.13.459-.056.15.073.245.227.24.395-.059 2.27-1.04 4.4-2.528 5.948-.032.053-.077.099-.131.134-1.434 1.43-3.31 2.331-5.277 2.331zm-3.299-1.829c1.002.618 2.133.977 3.299.977s2.296-.359 3.298-.977c-.86.15-1.981.278-3.298.278s-2.439-.128-3.299-.278zm-1.517-1.234c.409.13 2.23.66 4.816.66 2.583 0 4.404-.529 4.815-.66.67-.7 1.225-1.534 1.619-2.45-1.12.53-3.185 1.205-6.423 1.205-3.248 0-5.323-.68-6.447-1.21.394.918.95 1.753 1.62 2.455zm-2.03-3.659c.455.309 2.568 1.561 6.857 1.561 4.287 0 6.381-1.25 6.834-1.56.067-.264.12-.532.16-.804-1.748 1.128-4.099 1.7-7.005 1.7-2.907 0-5.258-.572-7.006-1.7.04.271.093.54.16.803zm-2.125-1.48c-.053 0-.107-.01-.16-.031-.218-.088-.324-.337-.236-.555l.02-.05c.398-.992 1.28-1.57 2.198-1.437.233.034.394.25.36.483-.034.233-.251.395-.483.36-.519-.074-1.035.291-1.283.912l-.02.05c-.067.167-.227.268-.396.268zm18.091 0c-.17 0-.33-.102-.396-.269l-.02-.05c-.248-.62-.762-.985-1.283-.91-.231.034-.45-.128-.483-.36-.034-.234.128-.45.36-.484.916-.134 1.8.444 2.198 1.439l.02.05c.087.218-.02.466-.238.553-.052.02-.106.03-.158.03zm-3.054-4.344c-.057 0-.115-.012-.17-.036l-3.781-1.654c-.155-.068-.256-.221-.256-.39 0-.17.1-.324.256-.391l3.78-1.654c.216-.095.467.004.562.22.094.215-.004.466-.22.56l-2.888 1.264 2.888 1.264c.216.094.314.346.22.561-.07.16-.227.256-.391.256zm-12.109 0c-.164 0-.32-.096-.39-.256-.095-.216.003-.467.219-.561l2.888-1.264-2.888-1.263c-.216-.095-.314-.346-.22-.562.095-.215.346-.313.562-.22l3.78 1.655c.156.067.256.22.256.39s-.1.323-.255.39l-3.781 1.655c-.056.024-.114.036-.17.036z"/>
  </g>
  <g clip-path="url(#prefix__clip1)">
      <path fill="#fff" d="M34 22c-.364 0-.706.098-1 .269V22c0-1.103-.897-2-2-2-.364 0-.706.098-1 .269V20c0-1.103-.897-2-2-2s-2 .897-2 2v.269c-.294-.171-.636-.269-1-.269-1.103 0-2 .897-2 2v5.501c-.418-.314-.937-.501-1.5-.501-.827 0-1.5.673-1.5 1.5v4.511c0 2.383.928 4.624 2.613 6.31l1.654 1.654c.652.651 1.553 1.025 2.475 1.025H30.5c3.032 0 5.5-2.467 5.5-5.5V24c0-1.103-.897-2-2-2z"/>
      <path fill="#222" d="M34 22c-.364 0-.706.098-1 .269V22c0-1.103-.897-2-2-2-.364 0-.706.098-1 .269V20c0-1.103-.897-2-2-2s-2 .897-2 2v.269c-.294-.171-.636-.269-1-.269-1.103 0-2 .897-2 2v5.501c-.418-.314-.937-.501-1.5-.501-.827 0-1.5.673-1.5 1.5v4.511c0 2.383.928 4.624 2.613 6.31l1.654 1.654c.652.651 1.553 1.025 2.475 1.025H30.5c3.032 0 5.5-2.467 5.5-5.5V24c0-1.103-.897-2-2-2zm1 14.5c0 2.481-2.019 4.5-4.5 4.5h-3.758c-.658 0-1.303-.267-1.768-.733l-1.654-1.654C21.824 37.116 21 35.127 21 33.011V28.5c0-.276.225-.5.5-.5.827 0 1.5.673 1.5 1.5v3c0 .276.224.5.5.5s.5-.224.5-.5V22c0-.551.448-1 1-1s1 .449 1 1v6.5c0 .276.224.5.5.5s.5-.224.5-.5V20c0-.551.448-1 1-1s1 .449 1 1v8.5c0 .276.224.5.5.5s.5-.224.5-.5V22c0-.551.448-1 1-1s1 .449 1 1v6.5c0 .276.224.5.5.5s.5-.224.5-.5V24c0-.551.448-1 1-1s1 .449 1 1v12.5z"/>
  </g>
  <path fill="#E7504E" d="M24 0C10.766 0 0 10.765 0 24c0 13.234 10.766 24 24 24s24-10.766 24-24C48 10.765 37.234 0 24 0zM3 24c0-5.25 1.95-10.045 5.148-13.731L37.73 39.852C34.045 43.05 29.251 45 24 45 12.421 45 3 35.579 3 24zm36.852 13.731L10.27 8.148C13.955 4.95 18.75 3 24 3c11.58 0 21 9.421 21 21 0 5.25-1.95 10.045-5.148 13.731z"/>
  <defs>
      <clipPath id="prefix__clip0">
          <path fill="#fff" d="M6 6H42V42H6z"/>
      </clipPath>
      <clipPath id="prefix__clip1">
          <path fill="#fff" d="M16 18H40V42H16z"/>
      </clipPath>
  </defs>
  </svg>;
}

function SvgLightningIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 36 36">
    <path fill="#222" stroke="#fff" stroke-width=".12" d="M18.606 3.333c0 .336-.27.607-.606.607-.336 0-.607-.271-.607-.607V.667c0-.336.271-.607.607-.607.335 0 .606.271.606.607v2.666zM31.333 16.06h2.666c.336 0 .607.271.607.607 0 .335-.271.606-.607.606h-2.666c-.336 0-.607-.27-.607-.606 0-.336.271-.607.607-.607zM2 16.06h2.666c.336 0 .607.271.607.607 0 .335-.271.606-.607.606H2c-.336 0-.607-.27-.607-.606 0-.336.271-.607.607-.607zM27 6.81l1.885-1.886c.237-.237.62-.237.858 0 .237.237.237.62 0 .858l-1.885 1.885c-.119.118-.274.178-.43.178-.155 0-.31-.06-.428-.178-.238-.237-.238-.62 0-.858zM6.256 27.552l1.886-1.886c.237-.237.62-.237.858 0 .237.238.237.621 0 .858L7.114 28.41c-.118.118-.273.177-.429.177-.155 0-.31-.06-.429-.177-.237-.238-.237-.621 0-.858zM27 25.666c.237-.237.62-.237.858 0l1.885 1.886c.237.237.237.62 0 .858-.118.118-.274.177-.43.177-.154 0-.31-.06-.428-.177L27 26.524c-.238-.237-.238-.62 0-.858zM8.57 7.845c-.155 0-.31-.06-.428-.178L6.256 5.782c-.237-.238-.237-.621 0-.858.238-.237.621-.237.858 0L9 6.809c.237.237.237.62 0 .858-.119.118-.274.178-.43.178zM10.06 16.667c0-4.379 3.562-7.94 7.94-7.94.335 0 .607.27.607.606 0 .336-.271.607-.607.607-3.71 0-6.727 3.018-6.727 6.727 0 .335-.27.606-.606.606-.336 0-.607-.27-.607-.606z"/>
    <path fill="#222" stroke="#fff" stroke-width=".12" d="M14.06 27.267v-.042l-.04-.015c-4.382-1.65-7.294-5.827-7.294-10.543 0-6.217 5.057-11.274 11.273-11.274 6.216 0 11.274 5.058 11.274 11.274 0 4.716-2.912 8.894-7.295 10.543l-.039.015V33.333c0 .336-.27.607-.606.607H20.606v1.393c0 .336-.271.607-.607.607h-4c-.335 0-.606-.271-.606-.607V33.94H14.666c-.335 0-.607-.271-.607-.607V27.267zm7.083-1.049h0c4.136-1.36 6.916-5.199 6.916-9.551 0-5.548-4.512-10.06-10.06-10.06-5.547 0-10.06 4.513-10.06 10.06 0 4.352 2.78 8.191 6.916 9.551h0c.25.082.418.315.418.577V28.06H20.726V26.795c0-.262.168-.495.417-.577z"/>
  </svg>;
}

function SvgDoneIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 16 16">
    <g clip-path="url(#a)">
        <path fill="#fff" d="M0 8.51L5.48 14 16 3.49 14.49 2l-9.01 9-3.99-3.99L0 8.51z"/>
    </g>
    <defs>
        <clipPath id="a">
            <path fill="#fff" d="M0 0h16v16H0z"/>
        </clipPath>
    </defs>
  </svg>;
}
